import intl from "react-intl-universal";
import { formatInputValue } from "../../../../../Utils/formatInputValue";
import {
  BAAS_DEFAULT_EFX_LIMIT_ROWS,
  BAAS_DEFAULT_NETWORK_FEE_ROWS,
  BAAS_DEFAULT_PIX_FEE_ROWS,
  BAAS_DEFAULT_PIX_LIMIT_ROWS,
  BAAS_DEFAULT_STABLES_FEE_ROWS,
  BAAS_DEFAULT_STABLES_LIMIT_ROWS,
  BAAS_RAIL_SECTION_TABS,
  pixFeeFixedDraftKey,
  pixFeeCostPercentDraftKey,
  pixFeeCostFixedDraftKey,
  pixLimitDraftKey,
  resolvePixFeeInstanceName,
  stablesLimitDraftKey,
} from "../baasInstanceCreateConstants";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const PIX_PERIODS = [
  { period: 1, labelKey: "baas.instances.createPage.limitsDay" },
  { period: 2, labelKey: "baas.instances.createPage.limitsNight" },
];

const PIX_AMOUNT_FIELDS = [
  { field: "amount", labelKey: "baas.pix.limits.dailyLabel" },
  { field: "monthly", labelKey: "baas.pix.limits.monthlyLabel" },
  { field: "perop", labelKey: "baas.pix.limits.perOpLabel" },
];

const uniqueStablesOps = [
  ...new Set(BAAS_DEFAULT_STABLES_LIMIT_ROWS.map((row) => row.operationType)),
];

const maskDraft = (setter) => (key, rawValue) => {
  setter((prev) => ({
    ...prev,
    [key]: formatInputValue(String(rawValue ?? "")),
  }));
};

const MoneyInput = ({ value, onChange, disabled, ariaLabel, prefix = "R$" }) => (
  <div className="relative">
    {prefix ? (
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
        {prefix}
      </span>
    ) : null}
    <input
      className={prefix ? `${inputClass} pl-9` : inputClass}
      inputMode="decimal"
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  </div>
);

/**
 * Unified Limites + Tarifas editor (Pix / Stables / EFX) for Nova instância
 * and Configuração. Matches Contas → Limites card fields: daily / monthly / per-op
 * plus % / fixed fees where the rail supports them.
 */
export const BaasInstanceRailLimitsPanel = ({
  railTab,
  onRailTabChange,
  pixDrafts,
  setPixDrafts,
  stablesDrafts,
  setStablesDrafts,
  efxDrafts,
  setEfxDrafts,
  pixFeeDrafts,
  setPixFeeDrafts,
  stablesFeeDrafts,
  setStablesFeeDrafts,
  efxFeeDrafts,
  setEfxFeeDrafts,
  networkFeeDrafts,
  setNetworkFeeDrafts,
  disabled = false,
  cloneHint = false,
  showEfx = true,
  instanceName = "",
}) => {
  const setPix = maskDraft(setPixDrafts);
  const setStables = maskDraft(setStablesDrafts);
  const setEfx = maskDraft(setEfxDrafts);
  const setPixFee = maskDraft(setPixFeeDrafts);
  const setStablesFee = maskDraft(setStablesFeeDrafts);
  const setEfxFee = maskDraft(setEfxFeeDrafts);
  const setNetworkFee = maskDraft(setNetworkFeeDrafts);
  const feeInstanceName = resolvePixFeeInstanceName(
    instanceName,
    intl.get("baas.instances.createPage.instanceFallbackName"),
  );
  const feeInstanceVars = { instanceName: feeInstanceName };

  return (
    <div className="space-y-4">
      {cloneHint ? (
        <p className="rounded-lg border border-amber-200/70 bg-amber-50/50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-100">
          {intl.get("baas.instances.createPage.limitsCloneHint")}
        </p>
      ) : null}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {intl.get("baas.instances.createPage.railFeesHint")}
      </p>

      <div
        className="flex flex-wrap gap-2"
        role="tablist"
        aria-label={intl.get("baas.instances.createPage.sectionLimits")}
      >
        {BAAS_RAIL_SECTION_TABS.filter((tab) => tab.id !== "efx" || showEfx).map((tab) => {
          const active = railTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                active
                  ? "border-[#e7a73f] bg-[#e7a73f]/15 text-brand-dark dark:text-gray-100"
                  : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300"
              }`}
              onClick={() => onRailTabChange(tab.id)}
              disabled={disabled}
            >
              {intl.get(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {railTab === "pix" ? (
        <div className="space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.createPage.pixRailHint")}
          </p>
          {BAAS_DEFAULT_PIX_LIMIT_ROWS.map((row) => {
            const opLabel = intl.get(`baas.instances.createPage.pixType.${row.code}`);
            const hasFee = BAAS_DEFAULT_PIX_FEE_ROWS.some(
              (fee) => fee.operationType === row.code,
            );
            return (
              <div
                key={row.type}
                className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40"
              >
                <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {opLabel}
                </p>
                {PIX_PERIODS.map((period) => (
                  <div key={period.period} className="space-y-2">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {intl.get(period.labelKey)}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {PIX_AMOUNT_FIELDS.map((field) => {
                        const key = pixLimitDraftKey(row.type, period.period, field.field);
                        return (
                          <div key={field.field}>
                            <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                              {intl.get(field.labelKey)}
                            </label>
                            <MoneyInput
                              value={pixDrafts[key]}
                              onChange={(raw) => setPix(key, raw)}
                              disabled={disabled}
                              ariaLabel={`${opLabel} — ${intl.get(period.labelKey)} — ${intl.get(field.labelKey)}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {hasFee ? (
                  <div className="space-y-3 border-t border-gray-200 pt-3 dark:border-brand-dark-border">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {intl.get("baas.instances.createPage.pixFeeCostHint", feeInstanceVars)}
                    </p>
                    <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      {intl.get("baas.instances.createPage.costValutGroup", feeInstanceVars)}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          {intl.get("baas.instances.createPage.costPercentLabel")}
                        </label>
                        <MoneyInput
                          prefix="%"
                          value={pixFeeDrafts[pixFeeCostPercentDraftKey(row.code)]}
                          onChange={(raw) => setPixFee(pixFeeCostPercentDraftKey(row.code), raw)}
                          disabled={disabled}
                          ariaLabel={`${opLabel} — ${intl.get("baas.instances.createPage.costPercentLabel")}`}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          {intl.get("baas.instances.createPage.costFixedLabel")}
                        </label>
                        <MoneyInput
                          value={pixFeeDrafts[pixFeeCostFixedDraftKey(row.code)]}
                          onChange={(raw) => setPixFee(pixFeeCostFixedDraftKey(row.code), raw)}
                          disabled={disabled}
                          ariaLabel={`${opLabel} — ${intl.get("baas.instances.createPage.costFixedLabel")}`}
                        />
                      </div>
                    </div>
                    <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      {intl.get("baas.instances.createPage.overlayGroup")}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          {intl.get("baas.instances.createPage.feePercentLabel")}
                        </label>
                        <MoneyInput
                          prefix="%"
                          value={pixFeeDrafts[row.code]}
                          onChange={(raw) => setPixFee(row.code, raw)}
                          disabled={disabled}
                          ariaLabel={`${opLabel} — ${intl.get("baas.instances.createPage.feePercentLabel")}`}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          {intl.get("baas.instances.createPage.feeFixedLabel")}
                        </label>
                        <MoneyInput
                          value={pixFeeDrafts[pixFeeFixedDraftKey(row.code)]}
                          onChange={(raw) => setPixFee(pixFeeFixedDraftKey(row.code), raw)}
                          disabled={disabled}
                          ariaLabel={`${opLabel} — ${intl.get("baas.instances.createPage.feeFixedLabel")}`}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {railTab === "stables" ? (
        <div className="space-y-5">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.createPage.stablesRailHint")}
          </p>
          {uniqueStablesOps.map((operationType) => {
            const opLabel = intl.get(`baas.instances.createPage.stablesOp.${operationType}`);
            const periodRows = BAAS_DEFAULT_STABLES_LIMIT_ROWS.filter(
              (row) => row.operationType === operationType,
            );
            const hasFee = BAAS_DEFAULT_STABLES_FEE_ROWS.some(
              (fee) => fee.operationType === operationType,
            );
            return (
              <div
                key={operationType}
                className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40"
              >
                <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {opLabel}
                </p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {periodRows.map((row) => {
                    const key = stablesLimitDraftKey(row.operationType, row.periodType);
                    const periodLabel = intl.get(
                      `baas.instances.createPage.limitsPeriod.${row.periodType}`,
                    );
                    return (
                      <div key={key}>
                        <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                          {periodLabel}
                        </label>
                        <MoneyInput
                          value={stablesDrafts[key]}
                          onChange={(raw) => setStables(key, raw)}
                          disabled={disabled}
                          ariaLabel={`${opLabel} — ${periodLabel}`}
                        />
                      </div>
                    );
                  })}
                  {hasFee ? (
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        {intl.get("baas.instances.createPage.feePercentLabel")}
                      </label>
                      <MoneyInput
                        prefix="%"
                        value={stablesFeeDrafts[operationType]}
                        onChange={(raw) => setStablesFee(operationType, raw)}
                        disabled={disabled}
                        ariaLabel={`${opLabel} — ${intl.get("baas.instances.createPage.feePercentLabel")}`}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}

          <div className="space-y-3 border-t border-gray-100 pt-4 dark:border-brand-dark-border">
            <p className="text-sm font-medium text-brand-dark dark:text-gray-100">
              {intl.get("baas.instances.createPage.feesTabNetwork")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.createPage.networkFeesHint")}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {BAAS_DEFAULT_NETWORK_FEE_ROWS.map((row) => {
                const netLabel = intl.get(`baas.instances.createPage.networkFee.${row.network}`);
                return (
                  <div key={row.network}>
                    <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                      {netLabel}
                    </label>
                    <MoneyInput
                      value={networkFeeDrafts[row.network]}
                      onChange={(raw) => setNetworkFee(row.network, raw)}
                      disabled={disabled}
                      ariaLabel={`${netLabel} — ${intl.get("baas.instances.createPage.networkFeeAmountLabel")}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {showEfx && railTab === "efx" ? (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.createPage.efxRailHint")}
          </p>
          {BAAS_DEFAULT_EFX_LIMIT_ROWS.map((row) => {
            const key = stablesLimitDraftKey(row.operationType, row.periodType);
            const opLabel = intl.get(`baas.instances.createPage.efxOp.${row.operationType}`);
            return (
              <div
                key={key}
                className="grid gap-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 sm:grid-cols-[minmax(8rem,1.2fr)_minmax(0,1fr)_minmax(0,0.85fr)] sm:items-end"
              >
                <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {opLabel}
                </p>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.limitsHourly")}
                  </label>
                  <MoneyInput
                    value={efxDrafts[key]}
                    onChange={(raw) => setEfx(key, raw)}
                    disabled={disabled}
                    ariaLabel={`${opLabel} — ${intl.get("baas.instances.createPage.limitsHourly")}`}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.feePercentLabel")}
                  </label>
                  <MoneyInput
                    prefix="%"
                    value={efxFeeDrafts[row.operationType]}
                    onChange={(raw) => setEfxFee(row.operationType, raw)}
                    disabled={disabled}
                    ariaLabel={`${opLabel} — ${intl.get("baas.instances.createPage.feePercentLabel")}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
